import React, { FC, useEffect } from 'react'

import { CookingTime, MarketableKind } from '@lojinha/palantir'
import { beholder } from '@lojinha/beholder'

import { DisplayItems } from '../../modules/page-content/page-content.types'
import { FeatureManager } from '../../FeatureManager'
import { Summary } from './summary'
import { KitsList } from './kits-list'
import { Details } from './details'
import { Carousel } from './carousel-display'
import { PREPARATION_METHODS } from './preparing-methods/constants'
import { HowToPrepare } from './how-to-prepare'

type Props = {
  item: DisplayItems
}

const hasCookingTime = (cookingTime: CookingTime) =>
  !!cookingTime?.minutes || !!cookingTime?.seconds

const getPreparationMethods = ({
  cookingTimeMicrowave,
  cookingTimeWaterBath,
  cookingTimeOven,
}: Record<string, CookingTime>) => {
  const preparationMethods: Array<keyof typeof PREPARATION_METHODS> = []

  if (hasCookingTime(cookingTimeMicrowave)) {
    preparationMethods.push(PREPARATION_METHODS.MICROWAVE)
  }
  if (hasCookingTime(cookingTimeWaterBath)) {
    preparationMethods.push(PREPARATION_METHODS.WATER_BATH)
  }
  if (hasCookingTime(cookingTimeOven)) {
    preparationMethods.push(PREPARATION_METHODS.OVEN)
  }
  return preparationMethods
}

export const MarketableDetails: FC<Props> = ({ item }) => {
  const {
    id,
    name,
    description,
    ingredients,
    products,
    allergenics,
    nutritionFacts,
    days,
    cookingTimeMicrowave,
    cookingTimeOven,
    cookingTimeWaterBath,
  } = item

  useEffect(() => {
    setTimeout(() => {
      beholder.shot('productPageView', {
        sku: item.sku,
      })
    }, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const preparationMethods =
    item.kind === 'PRODUCT'
      ? getPreparationMethods({
          cookingTimeOven: cookingTimeOven!,
          cookingTimeWaterBath: cookingTimeWaterBath!,
          cookingTimeMicrowave: cookingTimeMicrowave!,
        })
      : []

  return (
    <>
      <Summary item={item} />
      {item.kind === 'KIT' ? (
        <>
          <KitsList days={days} />
          {FeatureManager.isRecommendationEnabled && (
            <Carousel id={id} kind={MarketableKind.Kit} />
          )}
        </>
      ) : (
        <>
          {FeatureManager.isRecommendationEnabled && (
            <Carousel id={id} kind={MarketableKind.Product} />
          )}

          <Details
            id={id}
            name={name}
            ingredients={ingredients}
            description={description}
            products={products}
            allergenics={allergenics}
            nutritionFacts={nutritionFacts}
          />
        </>
      )}
      <HowToPrepare preparationMethods={preparationMethods} />
    </>
  )
}
